import React, { useMemo, useState } from 'react';
import './ForecastStrip.css';

const RISK_RANK = {
  Low: 1,
  Medium: 2,
  High: 3,
};

const REASON_TEXT = {
  weekday_pattern: ({ weekday }) =>
    `${weekday}s have a higher pattern in your saved check-ins, so this day uses your own weekday history.`,
  rising_stress: () =>
    'Your recent check-in scores are trending upward, so this day is flagged with extra caution.',
  declining_trend: () =>
    'Your recent check-in scores are trending downward, so this forecast reflects improving momentum.',
  stable: () =>
    'Your recent check-ins have been fairly stable, so this day uses your recent average.',
  population_default: () =>
    'There is not enough personal pattern for this day yet, so QuitPact uses a simple baseline.',
};

function formatShortDate(dateText) {
  const date = new Date(`${dateText}T00:00:00`);
  return date.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
  });
}

function formatWeekday(item) {
  if (item.weekday) return item.weekday.slice(0, 3);
  const date = new Date(`${item.date}T00:00:00`);
  return date.toLocaleDateString('en-AU', { weekday: 'short' });
}

const ForecastStrip = ({ forecast = [] }) => {
  const [expandedDate, setExpandedDate] = useState(null);

  const peakDate = useMemo(() => {
    if (!forecast.length) return null;

    return forecast.reduce((peak, item) => {
      if (!peak) return item;
      return (RISK_RANK[item.risk_level] || 0) > (RISK_RANK[peak.risk_level] || 0)
        ? item
        : peak;
    }, null)?.date;
  }, [forecast]);

  if (!Array.isArray(forecast) || forecast.length === 0) {
    return null;
  }

  const toggleDay = (date) => {
    setExpandedDate((current) => (current === date ? null : date));
  };

  const handleKeyDown = (event, date) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleDay(date);
    }
  };

  const expandedItem = forecast.find((item) => item.date === expandedDate);
  const reasonTemplate = expandedItem
    ? REASON_TEXT[expandedItem.reason_code] || REASON_TEXT.population_default
    : null;

  return (
    <section className="forecast-card" aria-label="Seven day risk forecast">
      <div className="forecast-card__header">
        <div>
          <h2 className="forecast-card__title">Seven day forecast</h2>
          <p className="forecast-card__subtitle">
            Projected from your saved check-in pattern.
          </p>
        </div>
      </div>

      <div className="forecast-strip" role="list">
        {forecast.map((item) => {
          const isPeak = item.date === peakDate;
          const isExpanded = item.date === expandedDate;

          return (
            <div key={item.date} role="listitem">
              <button
                type="button"
                className={`forecast-tile forecast-tile--${item.risk_level.toLowerCase()} ${isPeak ? 'forecast-tile--peak' : ''}`}
                onClick={() => toggleDay(item.date)}
                onKeyDown={(event) => handleKeyDown(event, item.date)}
                aria-expanded={isExpanded}
              >
                <span className="forecast-tile__weekday">{formatWeekday(item)}</span>
                <span className="forecast-tile__date">{formatShortDate(item.date)}</span>
                <span className="forecast-tile__risk">{item.risk_level}</span>
                {isPeak && <span className="forecast-tile__tag">Peak risk</span>}
              </button>
            </div>
          );
        })}
      </div>

      {expandedItem && (
        <div className="forecast-reason" role="status">
          <strong>{formatWeekday(expandedItem)}, {formatShortDate(expandedItem.date)}:</strong>{' '}
          {reasonTemplate(expandedItem.reason_context || {})}
        </div>
      )}
    </section>
  );
};

export default ForecastStrip;
